/**
 * F-131 wiring: specialist-side modal to send an OrderProposal into a chat.
 *
 * Flow:
 *   1. Pick a preset (collapses title/description/price/duration).
 *   2. Pick a free slot via <SlotPicker> (calls existing /specialists/<slug>/slots/).
 *   3. Confirm → POST /orders/ with chat_id + scheduled_at + preset_id.
 *      Backend creates the order in status=proposed and broadcasts an
 *      `order_proposal` system message via the chat consumer.
 */
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Button, Card, Icon, Modal, Spinner, Textarea, Input } from '@/components/atoms';
import { toast } from '@/components/atoms/Toast';
import type { ServicePreset } from '@/types/domain';
import { presets as presetsApi, orders as ordersApi } from '@/api';
import type { Slot } from '@/api/booking';
import { useAuthStore } from '@/store';
import { logger } from '@/utils/logger';
import { SlotPicker } from './SlotPicker';
import styles from './OrderProposalComposer.module.css';

export interface OrderProposalComposerProps {
  chatId: number;
  open: boolean;
  onClose: () => void;
  onSent?: (orderId: number) => void;
}

export function OrderProposalComposer({
  chatId,
  open,
  onClose,
  onSent,
}: OrderProposalComposerProps): JSX.Element | null {
  const me = useAuthStore((s) => s.user);
  const specialistSlug = me?.specialist_profile?.slug ?? null;
  const [presets, setPresets] = useState<ServicePreset[] | null>(null);
  const [selectedPreset, setSelectedPreset] = useState<ServicePreset | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priceEur, setPriceEur] = useState<number>(0);
  const [durationMin, setDurationMin] = useState<number>(60);
  const [slot, setSlot] = useState<Slot | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // F-180 C6: inline price validation instead of just disabling submit.
  const [priceError, setPriceError] = useState<string | undefined>();

  useEffect(() => {
    if (!open || presets !== null) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await presetsApi.fetchMyPresets();
        if (!cancelled) setPresets(data.results.filter((p) => p.is_active));
      } catch (err) {
        logger.warn('presets fetch failed', err);
        if (!cancelled) setPresets([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, presets]);

  useEffect(() => {
    if (selectedPreset) {
      setTitle(selectedPreset.title);
      setDescription(selectedPreset.description);
      setPriceEur(selectedPreset.price_eur);
      if (selectedPreset.duration_minutes) {
        setDurationMin(selectedPreset.duration_minutes);
      }
    }
  }, [selectedPreset]);

  if (!open) return null;

  const canSubmit =
    !!title.trim() && priceEur > 0 && durationMin > 0 && !!slot && !submitting;

  const submit = async (): Promise<void> => {
    if (priceEur <= 0) {
      setPriceError('Ціна має бути більше 0');
      return;
    }
    if (!canSubmit || !slot) return;
    setSubmitting(true);
    try {
      const order = await ordersApi.createOrder({
        chat_id: chatId,
        preset_id: selectedPreset?.id,
        title: title.trim(),
        description: description.trim(),
        price_eur: priceEur,
        duration_minutes: durationMin,
        scheduled_at: slot.start_at,
      });
      toast.success('Замовлення запропоновано');
      onSent?.(order.id);
      onClose();
    } catch (err) {
      logger.error('createOrder failed', err);
      toast.error('Не вдалось створити замовлення');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open title="Запропонувати замовлення" onClose={onClose}>
      {!specialistSlug ? (
        <Card>
          <p>Лише спеціалісти можуть надсилати пропозиції.</p>
        </Card>
      ) : (
        <div className={styles.stack}>
          <Card>
            <strong>Послуга</strong>
            {presets === null ? (
              <Spinner center />
            ) : presets.length === 0 ? (
              <p>Спочатку додай хоч один пресет у «Я → Пресети послуг».</p>
            ) : (
              <div className={styles.presetList}>
                {presets.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className={
                      selectedPreset?.id === p.id
                        ? `${styles.presetItem} ${styles.presetActive}`
                        : styles.presetItem
                    }
                    onClick={() => setSelectedPreset(p)}
                  >
                    <span className={styles.presetTitle}>{p.title}</span>
                    <span className={styles.presetMeta}>
                      {Number(p.price_eur).toFixed(2)} €
                      {p.duration_minutes ? ` · ${p.duration_minutes} хв` : ''}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </Card>

          {selectedPreset && (
            <Card>
              <Input
                label="Назва"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={120}
              />
              <Textarea
                label="Опис"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                maxLength={500}
                style={{ marginTop: 8 }}
              />
              <div className={styles.row}>
                <Input
                  label="Ціна (€)"
                  type="number"
                  min={0}
                  step={0.5}
                  value={priceEur}
                  error={priceError}
                  onChange={(e) => {
                    setPriceEur(Number(e.target.value));
                    setPriceError(undefined);
                  }}
                />
                <Input
                  label="Тривалість (хв)"
                  type="number"
                  min={15}
                  step={15}
                  value={durationMin}
                  onChange={(e) => setDurationMin(Number(e.target.value))}
                />
              </div>
            </Card>
          )}

          {selectedPreset && (
            <Card>
              <strong style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Icon name="calendar" size={18} />
                Календар — обери час
              </strong>
              <SlotPicker
                specialistSlug={specialistSlug}
                onConfirm={(s) => setSlot(s)}
                onCancel={() => setSlot(null)}
              />
              {slot && (
                <p className={styles.confirmedSlot}>
                  Обрано: {dayjs(slot.start_at).format('D MMM, HH:mm')}
                </p>
              )}
            </Card>
          )}

          <div className={styles.actions}>
            <Button variant="ghost" onClick={onClose}>
              Скасувати
            </Button>
            <Button onClick={submit} disabled={!canSubmit}>
              {submitting ? 'Надсилаю…' : 'Запропонувати'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
